'use client'

import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { useTranslations } from 'next-intl'
import { useTimer } from 'react-timer-hook'

const countdownBoxVariants = cva('flex items-center', {
  variants: {
    size: {
      small: 'gap-2',
      default: 'gap-1.5 md:gap-3 lg:gap-3 xl:gap-4 2xl:gap-4',
      medium: 'gap-2 md:gap-3.5 lg:gap-4 xl:gap-4 2xl:gap-5',
      large: 'gap-2 md:gap-4 lg:gap-4 xl:gap-5 2xl:gap-6',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const countdownItemVariants = cva('flex flex-col items-center', {
  variants: {
    size: {
      small: '',
      default: '',
      medium: '',
      large: '',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const countdownNumberBoxVariants = cva(
  'flex items-center justify-center rounded-lg bg-white font-criteria font-bold text-neutral-900',
  {
    variants: {
      size: {
        small: 'h-10 w-10 text-xl',
        default:
          'h-10 w-10 text-lg md:h-[72px] md:w-[72px] md:text-3xl lg:h-[80px] lg:w-[80px] xl:h-[88px] xl:w-[88px] xl:text-4xl 2xl:h-[96px] 2xl:w-[96px] 2xl:text-4xl',
        medium:
          'h-12 w-12 text-lg md:h-20 md:w-20 md:text-3xl lg:h-24 lg:w-24 lg:text-3xl xl:h-28 xl:w-28 xl:text-4xl 2xl:h-32 2xl:w-32 2xl:text-5xl',
        large:
          'h-14 w-14 text-xl md:h-24 md:w-24 md:text-4xl lg:h-28 lg:w-28 lg:text-4xl xl:h-32 xl:w-32 xl:text-5xl 2xl:h-36 2xl:w-36 2xl:text-6xl',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const countdownLabelVariants = cva('mt-1 font-normal text-white', {
  variants: {
    size: {
      small: 'text-xs',
      default: 'text-[10px] md:text-sm lg:text-sm xl:text-base 2xl:text-base',
      medium: 'text-[10px] md:text-sm lg:text-sm xl:text-base 2xl:text-lg',
      large: 'text-xs md:text-base lg:text-base xl:text-lg 2xl:text-xl',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

interface CountDownBoxProps extends VariantProps<typeof countdownBoxVariants> {
  expiryTimestamp: Date
  onExpire?: () => void
  className?: string
}

export function CountDownBox({
  expiryTimestamp,
  onExpire,
  className = '',
  size = 'default',
}: CountDownBoxProps) {
  const t = useTranslations('home_page.countdown')
  const { days, hours, minutes, seconds, isRunning } = useTimer({
    expiryTimestamp,
    onExpire,
    autoStart: true,
  })

  if (!isRunning) {
    return null
  }

  const formatNumber = (num: number) => num.toString().padStart(2, '0')

  return (
    <div className={cn(countdownBoxVariants({ size }), className)}>
      {/* Days Box */}
      <div className={countdownItemVariants({ size })}>
        <div className={countdownNumberBoxVariants({ size })}>
          {formatNumber(days)}
        </div>
        <span className={countdownLabelVariants({ size })}>{t('days')}</span>
      </div>

      {/* Hours Box */}
      <div className={countdownItemVariants({ size })}>
        <div className={countdownNumberBoxVariants({ size })}>
          {formatNumber(hours)}
        </div>
        <span className={countdownLabelVariants({ size })}>{t('hours')}</span>
      </div>

      {/* Minutes Box */}
      <div className={countdownItemVariants({ size })}>
        <div className={countdownNumberBoxVariants({ size })}>
          {formatNumber(minutes)}
        </div>
        <span className={countdownLabelVariants({ size })}>{t('mins')}</span>
      </div>

      {/* Seconds Box */}
      <div className={countdownItemVariants({ size })}>
        <div className={countdownNumberBoxVariants({ size })}>
          {formatNumber(seconds)}
        </div>
        <span className={countdownLabelVariants({ size })}>{t('secs')}</span>
      </div>
    </div>
  )
}
